import { normalizeAssetLookupKey } from '../utils/bookingAssetThumbnailCache';
import { decodeJwtPayload } from '../auth/jwtPayload';
import { getSessionToken } from '../auth/sessionToken';
import { prepareRecordForThumbnailLookup } from '../utils/assetImageResolve';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { assetRecordMutationId, assetRecordPrimaryId, isUuidLikeAssetId } from '../utils/assetRecordPrimaryId';

/** Guest / legacy local favourites (used once to migrate onto the server). */
export const FAVORITE_ASSETS_STORAGE_KEY = 'ofrontend.favoriteAssets';

const FAVORITE_USER_STORAGE_PREFIX = 'ofrontend.favoriteAssets.user:';
const FAVORITE_MIGRATED_PREFIX = 'ofrontend.favoriteAssets.migrated:';
const FAVORITE_PENDING_SYNC_KEY = 'ofrontend.favoriteAssets.pendingSync';
const FAVORITE_PENDING_REMOVAL_KEY = 'ofrontend.favoriteAssets.pendingRemoval';

/** True for guest/user favourites buckets and sync queues — kept during cache clear. */
export function isFavoriteAssetsStorageKey(key: string): boolean {
  const trimmed = key.trim();
  if (!trimmed) {
    return false;
  }
  return (
    trimmed === FAVORITE_ASSETS_STORAGE_KEY || trimmed.startsWith(`${FAVORITE_ASSETS_STORAGE_KEY}.`)
  );
}

export type FavoriteAssetEntry = {
  id: string;
  asset: Record<string, unknown>;
  savedAt: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Drop empty `images` so list thumbnails can fall back to scalar image keys from search rows. */
export function prepareFavoriteAssetSnapshot(asset: Record<string, unknown>): Record<string, unknown> {
  return prepareRecordForThumbnailLookup(coalesceAssetListRecord(asset));
}

function parseFavoriteEntries(raw: string | null): FavoriteAssetEntry[] {
  if (!raw?.trim()) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const out: FavoriteAssetEntry[] = [];
    for (const item of parsed) {
      if (!isRecord(item)) {
        continue;
      }
      const id = typeof item.id === 'string' ? item.id.trim() : '';
      const savedAt = typeof item.savedAt === 'number' && Number.isFinite(item.savedAt) ? item.savedAt : 0;
      const asset = isRecord(item.asset) ? item.asset : null;
      if (!id || !asset) {
        continue;
      }
      out.push({ id, asset: prepareFavoriteAssetSnapshot(asset), savedAt });
    }
    return out.sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

/** Stable account key for favourites cache / one-time local→server migration. */
export function favoriteAccountStorageKey(accessToken?: string | null): string | null {
  const token = (accessToken ?? getSessionToken())?.trim();
  if (!token) {
    return null;
  }
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }
  const sub =
    (typeof payload.sub === 'string' && payload.sub.trim()) ||
    (typeof payload.client_id === 'string' && payload.client_id.trim()) ||
    (typeof payload.user_id === 'string' && payload.user_id.trim()) ||
    (typeof payload.uid === 'string' && payload.uid.trim()) ||
    '';
  return sub ? sub.trim().toLowerCase() : null;
}

function storageKeyForAccount(accountKey: string | null | undefined): string {
  const key = accountKey?.trim();
  if (!key) {
    return FAVORITE_ASSETS_STORAGE_KEY;
  }
  return `${FAVORITE_USER_STORAGE_PREFIX}${key}`;
}

export function readFavoriteAssets(accountKey?: string | null): FavoriteAssetEntry[] {
  try {
    if (typeof localStorage === 'undefined') {
      return [];
    }
    return parseFavoriteEntries(localStorage.getItem(storageKeyForAccount(accountKey)));
  } catch {
    return [];
  }
}

/** Guest / legacy local favourites (used once to migrate onto the server). */
export function readGuestFavoriteAssets(): FavoriteAssetEntry[] {
  return readFavoriteAssets(null);
}

/** Every favourites cache bucket on this device (guest + all signed-in users). */
export function readAllLocalFavoriteEntries(): FavoriteAssetEntry[] {
  const seen = new Set<string>();
  const out: FavoriteAssetEntry[] = [];

  const addEntries = (entries: readonly FavoriteAssetEntry[]) => {
    for (const entry of entries) {
      const id = entry.id.trim();
      if (!id) {
        continue;
      }
      const key = normalizeAssetLookupKey(id);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(entry);
    }
  };

  addEntries(readGuestFavoriteAssets());
  addEntries(readFavoriteAssets(favoriteAccountStorageKey()));

  try {
    if (typeof localStorage !== 'undefined') {
      for (let index = 0; index < localStorage.length; index += 1) {
        const storageKey = localStorage.key(index);
        if (!storageKey?.startsWith(FAVORITE_USER_STORAGE_PREFIX)) {
          continue;
        }
        addEntries(parseFavoriteEntries(localStorage.getItem(storageKey)));
      }
    }
  } catch {
    /* ignore */
  }

  return out.sort((a, b) => b.savedAt - a.savedAt);
}

export function readPendingFavoriteSyncIds(): string[] {
  try {
    if (typeof localStorage === 'undefined') {
      return [];
    }
    const raw = localStorage.getItem(FAVORITE_PENDING_SYNC_KEY);
    if (!raw?.trim()) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());
  } catch {
    return [];
  }
}

export function addPendingFavoriteSyncId(id: string): void {
  const trimmed = id.trim();
  if (!trimmed) {
    return;
  }
  const current = readPendingFavoriteSyncIds();
  const pendingKey = normalizeAssetLookupKey(trimmed);
  if (current.some((existing) => normalizeAssetLookupKey(existing) === pendingKey)) {
    return;
  }
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(FAVORITE_PENDING_SYNC_KEY, JSON.stringify([trimmed, ...current]));
  } catch {
    /* quota / private mode */
  }
}

export function removePendingFavoriteSyncIds(ids: readonly string[]): void {
  if (ids.length === 0) {
    return;
  }
  const removeKeys = new Set<string>();
  for (const id of ids) {
    const trimmed = id.trim();
    if (!trimmed) {
      continue;
    }
    removeKeys.add(trimmed.toLowerCase());
    removeKeys.add(normalizeAssetLookupKey(trimmed));
  }
  const next = readPendingFavoriteSyncIds().filter((id) => {
    const keys = [id.toLowerCase(), normalizeAssetLookupKey(id)];
    return !keys.some((key) => removeKeys.has(key));
  });
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    if (next.length === 0) {
      localStorage.removeItem(FAVORITE_PENDING_SYNC_KEY);
      return;
    }
    localStorage.setItem(FAVORITE_PENDING_SYNC_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function readPendingFavoriteRemovalIds(): string[] {
  try {
    if (typeof localStorage === 'undefined') {
      return [];
    }
    const raw = localStorage.getItem(FAVORITE_PENDING_REMOVAL_KEY);
    if (!raw?.trim()) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());
  } catch {
    return [];
  }
}

export function addPendingFavoriteRemovalId(id: string): void {
  const trimmed = id.trim();
  if (!trimmed) {
    return;
  }
  removePendingFavoriteSyncIds([trimmed]);
  const current = readPendingFavoriteRemovalIds();
  const pendingKey = normalizeAssetLookupKey(trimmed);
  if (current.some((existing) => normalizeAssetLookupKey(existing) === pendingKey)) {
    return;
  }
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(FAVORITE_PENDING_REMOVAL_KEY, JSON.stringify([trimmed, ...current]));
  } catch {
    /* quota / private mode */
  }
}

export function removePendingFavoriteRemovalIds(ids: readonly string[]): void {
  if (ids.length === 0) {
    return;
  }
  const removeKeys = new Set<string>();
  for (const id of ids) {
    const trimmed = id.trim();
    if (!trimmed) {
      continue;
    }
    removeKeys.add(trimmed.toLowerCase());
    removeKeys.add(normalizeAssetLookupKey(trimmed));
  }
  const next = readPendingFavoriteRemovalIds().filter((id) => {
    const keys = [id.toLowerCase(), normalizeAssetLookupKey(id)];
    return !keys.some((key) => removeKeys.has(key));
  });
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    if (next.length === 0) {
      localStorage.removeItem(FAVORITE_PENDING_REMOVAL_KEY);
      return;
    }
    localStorage.setItem(FAVORITE_PENDING_REMOVAL_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function writeFavoriteAssets(
  entries: FavoriteAssetEntry[],
  accountKey?: string | null,
): void {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(storageKeyForAccount(accountKey), JSON.stringify(entries));
  } catch {
    /* quota / private mode */
  }
}

export function clearGuestFavoriteAssets(): void {
  writeFavoriteAssets([], null);
}

export function hasMigratedLocalFavorites(accountKey: string): boolean {
  try {
    if (typeof localStorage === 'undefined' || !accountKey.trim()) {
      return false;
    }
    return localStorage.getItem(`${FAVORITE_MIGRATED_PREFIX}${accountKey.trim()}`) === '1';
  } catch {
    return false;
  }
}

export function markLocalFavoritesMigrated(accountKey: string): void {
  try {
    if (typeof localStorage === 'undefined' || !accountKey.trim()) {
      return;
    }
    localStorage.setItem(`${FAVORITE_MIGRATED_PREFIX}${accountKey.trim()}`, '1');
  } catch {
    /* ignore */
  }
}

export function favoriteAssetSnapshotIsComplete(asset: Record<string, unknown>): boolean {
  const name =
    (typeof asset.asset_name === 'string' && asset.asset_name.trim()) ||
    (typeof asset.facility_name === 'string' && asset.facility_name.trim()) ||
    (typeof asset.name === 'string' && asset.name.trim()) ||
    '';
  if (!name || isUuidLikeAssetId(name)) {
    return false;
  }
  return true;
}

export function favoriteListRowFromEntry(entry: FavoriteAssetEntry): Record<string, unknown> {
  const asset = prepareFavoriteAssetSnapshot(entry.asset);
  const mutationId = assetRecordMutationId(asset) ?? entry.id.trim();
  const primaryId = assetRecordPrimaryId(asset) ?? mutationId;
  if (!mutationId && !primaryId) {
    return asset;
  }
  return {
    ...asset,
    id: primaryId,
    asset_id: mutationId,
    assetId: mutationId,
  };
}

export function favoriteIdForAsset(asset: Record<string, unknown>): string | null {
  return assetRecordMutationId(coalesceAssetListRecord(asset));
}

export function favoriteIdsMatch(a: string, b: string): boolean {
  const left = a.trim();
  const right = b.trim();
  if (!left || !right) {
    return false;
  }
  if (left === right) {
    return true;
  }
  return normalizeAssetLookupKey(left) === normalizeAssetLookupKey(right);
}

export function isFavoriteAssetId(
  id: string,
  entries: FavoriteAssetEntry[] = readFavoriteAssets(),
): boolean {
  const trimmed = id.trim();
  if (!trimmed) {
    return false;
  }
  return entries.some((entry) => favoriteIdsMatch(entry.id, trimmed));
}
